import { EmblaOptionsType } from 'embla-carousel';
import { usePrevNextButtons } from './carousel-button';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'components/ui/checkbox';
import { Button } from 'components/ui/button';

import SitComfortably from 'assets/images/face-scan/sit-comfortably.png';
import Light from 'assets/images/face-scan/light.png';
import Posture from 'assets/images/face-scan/posture.png';

export const SHOW_MEDSI_SCAN_INTRO_KEY = 'showMedsiScanIntro';

type MedsiScanIntroProps = {
  options?: EmblaOptionsType;
  onFinish: () => void;
};

export default function MedsiScanIntro({
  onFinish,
  options,
}: MedsiScanIntroProps) {
  const { t } = useTranslation();
  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [showAgain, setShowAgain] = useState(false);

  const slides = useMemo(
    () => [
      {
        id: 'sit-comfortably',
        img: SitComfortably,
        text: t('sit_comfortably'),
      },
      {
        id: 'light',
        img: Light,
        text: t('face_is_evenly_illuminated'),
      },
      {
        id: 'posture',
        img: Posture,
        text: t('avoid_moving_throughout_measurement'),
      },
    ],
    [t],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const { nextBtnDisabled, onNextButtonClick } = usePrevNextButtons(emblaApi);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const onNext = useCallback(() => {
    if (nextBtnDisabled) {
      localStorage.setItem(SHOW_MEDSI_SCAN_INTRO_KEY, String(!showAgain));
      return onFinish();
    }

    onNextButtonClick();
  }, [nextBtnDisabled, onNextButtonClick, onFinish, showAgain]);

  return (
    <div className='w-full flex flex-col gap-4 max-w-3xl mx-auto h-[calc(100svh-4rem)] pb-4'>
      <div
        className='py-3 bg-transparent'
        style={{
          boxShadow: '0px 0px 17.8px -4px #0000003B',
        }}
      >
        <h2 className='text-base font-medium text-center'>
          {t('medsi_how_to_use')}
        </h2>
        <div className='flex items-center justify-center gap-3'>
          <Checkbox
            checked={showAgain}
            onCheckedChange={(checked: boolean) => setShowAgain(checked)}
            className='border-primary'
          />
          <p className='text-sm'>{t('medsi_dont_show_again')}</p>
        </div>
      </div>

      <section className='flex flex-col flex-1 gap-4'>
        <div className='flex-1 w-full pl-6 pr-6 overflow-hidden' ref={emblaRef}>
          <div className='flex touch-[pan-y_pinch-zoom] gap-4 h-full'>
            {slides.map(slide => (
              <div
                className='transform translate-x-0 translate-y-0 translate-z-0 flex-[0_0_85%] min-w-0 border border-primary h-full rounded-xl overflow-hidden'
                key={slide.id}
              >
                <div
                  className='flex flex-col items-center justify-center h-full gap-4 p-4'
                  style={{
                    backdropFilter: 'blur(30.5px)',
                    boxShadow: '0px 0px 17.8px -4px #0000003B',
                    border: '1px solid',
                    borderImageSource:
                      'linear-gradient(38.96deg, rgba(108, 14, 54, 0.42) 1.27%, #F2E9ED 57.65%, rgba(108, 14, 54, 0.42) 95.39%)',
                  }}
                >
                  <div className='w-full h-[calc(100%-7rem)] aspect-square'>
                    <img
                      src={slide.img}
                      alt={slide.id}
                      className='object-contain w-full h-full'
                    />
                  </div>
                  <p className='text-sm font-medium leading-tight text-center'>
                    {slide.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='flex items-center justify-center gap-2'>
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollTo(idx)}
              className={`h-2 rounded-full transition-all ${
                idx === selectedIndex ? 'w-6 bg-primary' : 'w-2 bg-gray-300'
              }`}
              aria-label={t('go_to_slide', { number: idx + 1 })}
            />
          ))}
        </div>
      </section>

      <Button onClick={onNext} className='mx-4'>
        {t('next')}
      </Button>
    </div>
  );
}
